import { Router, watchBusy } from "@frui.ts/screens";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import { bound } from "@frui.ts/helpers";
import { Modules } from "config";
import type { interfaces } from "inversify";
import { computed, observable, reaction } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import OutageRepository from "repositories/outageRepository";
import SystemComponent from "entities/systemComponent";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import EditableDetailViewModel from "../../editableDetailViewModel";
import { getOutageMessage } from "./utils";
import OutageState from "entities/outageState";
import SystemComponentCode from "entities/systemComponentCode";
import type SystemComponentOutageResponse from "entities/systemComponentOutageResponse";
import { OutageValidationRules } from "entities/systemComponentOutageRequest";

@Router.registerRoute({ name: Router.Self, route: ":outageId" })
export default class OutageDetailViewModel extends EditableDetailViewModel<SystemComponentOutageResponse> {
  readonly modelName = "outage";
  @observable outageState: OutageState = OutageState.ACTIVE;
  private unsubscriptions: Array<() => void> = [];

  @computed
  get outageMessage() {
    if (this.item) {
      const message = this.translate(
        `messages.${this.systemComponentCode}.${this.outageState}.${this.item.validUntil ? "validUntil" : "validInfinity"}`
      );
      return getOutageMessage(this.item, message);
    }
    return undefined;
  }

  @computed
  get impact() {
    return this.translate(`messages.${this.systemComponentCode}.impact.${this.outageState}`);
  }

  @computed
  get systemComponentCode(): SystemComponentCode {
    const code: SystemComponentCode | string | undefined = this.optionsService
      .optionsViewModel(SystemComponent)
      .items.find(item => item.id === this.item?.systemComponentId)?.code;

    if (code === undefined || !Object.values(SystemComponentCode).includes(code as SystemComponentCode)) {
      return SystemComponentCode.OTHER;
    } else {
      return code as SystemComponentCode;
    }
  }

  constructor(
    protected itemId: string,
    protected repository: OutageRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected securityService: SecurityService,
    private optionsService: OptionsService
  ) {
    super(itemId);
  }

  async onInitialize(): Promise<void> {
    await super.onInitialize();
    this.unsubscriptions.push(
      reaction(() => this.item?.systemComponentId, this.onSystemComponentChange),
      reaction(() => [this.item?._visibleSinceTime], this.setVisibleSince),
      reaction(() => [this.item?._validSinceTime], this.setValidSince),
      reaction(() => [this.item?._validUntilTime], this.setValidUntil)
    );

    this.onSystemComponentChange();
  }

  protected onDeactivate(isClosing: boolean): Promise<any> | void {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  protected onActivate() {
    if (this.item) {
      this.onValidSinceTime();
      this.onValidUntilTIme();
      this.onVisibleSinceTime();
    }
    return super.onActivate();
  }

  @computed
  get systemComponentOptions() {
    return this.optionsService.entityOptions(SystemComponent);
  }

  async loadDetail() {
    const item = await this.repository.get(this.itemId);
    attachAutomaticValidator(item, OutageValidationRules);
    return item;
  }

  @bound
  setTimeFromString(date: Date) {
    return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}:${date
      .getSeconds()
      .toString()
      .padStart(2, "0")}`;
  }

  @bound
  onValidSinceTime() {
    this.item._validSinceTime = this.item.validSince !== undefined ? this.setTimeFromString(this.item.validSince) : "00:00:00";
  }
  @bound
  onValidUntilTIme() {
    if (this.item.validUntil) {
      this.item._validUntilTime = this.setTimeFromString(this.item.validUntil);
    }
  }
  @bound
  onVisibleSinceTime() {
    this.item._visibleSinceTime = this.item.visibleSince ? this.setTimeFromString(this.item.visibleSince) : "00:00:00";
  }

  @bound
  onSystemComponentChange() {
    // If we have translated outages set, but the component is not Other, reset the translations
    // so the "description" field is used instead.
    if (
      this.item.systemComponentOutageLocalizations &&
      this.item.systemComponentOutageLocalizations.length > 0 &&
      this.systemComponentCode !== SystemComponentCode.OTHER
    ) {
      this.item.systemComponentOutageLocalizations = undefined;
    }
    // Reset description if we're switching to Other component
    if (this.item.description && this.item.description.length > 0 && this.systemComponentCode === SystemComponentCode.OTHER) {
      this.item.description = undefined;
    }
  }

  @bound
  private setValidSince() {
    if (!this.item._validSinceTime) {
      this.item._validSinceTime = undefined;
    }
    if (!this.item._validSinceTime || !this.item.validSince) {
      return false;
    }
    const [hours, minutes, seconds] = this.item._validSinceTime.split(":");
    const timeDate = new Date(this.item.validSince);
    timeDate.setHours(parseInt(hours, 10));
    timeDate.setMinutes(parseInt(minutes, 10));
    timeDate.setSeconds(parseInt(seconds, 10));
    this.item.validSince = timeDate;
    return true;
  }

  @bound
  private setVisibleSince() {
    if (!this.item._visibleSinceTime) {
      this.item._visibleSinceTime = undefined;
    }
    if (this.item._visibleSinceTime === undefined || !this.item.visibleSince) {
      return false;
    }
    const [hours, minutes, seconds] = this.item._visibleSinceTime.split(":");
    const timeDate = new Date(this.item.visibleSince);
    timeDate.setHours(parseInt(hours, 10));
    timeDate.setMinutes(parseInt(minutes, 10));
    timeDate.setSeconds(parseInt(seconds, 10));
    this.item.visibleSince = timeDate;
    return true;
  }

  @bound
  private setValidUntil() {
    if (!this.item._validUntilTime) {
      this.item._validUntilTime = undefined;
    }
    if (!this.item._validUntilTime || !this.item.validUntil) {
      return false;
    }
    const [hours, minutes, seconds] = this.item._validUntilTime.split(":");
    const timeDate = new Date(this.item.validUntil);
    timeDate.setHours(parseInt(hours, 10));
    timeDate.setMinutes(parseInt(minutes, 10));
    timeDate.setSeconds(parseInt(seconds, 10));
    this.item.validUntil = timeDate;
    return true;
  }

  @bound
  @watchBusy
  async save() {
    if (!validate(this.item)) {
      return;
    }
    this.setValidSince();
    this.setVisibleSince();
    this.setValidUntil();
    await super.save();
  }

  updateData(): Promise<SystemComponentOutageResponse> {
    return this.repository.update(this.item);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.administration, `outages.${code}`, size);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) =>
      new OutageDetailViewModel(
        itemId,
        container.get(OutageRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(OptionsService)
      );
  }
}
